import { styled } from '@mui/material/styles';
import { Box, ButtonBase } from '@mui/material';

import { MINT_MIST } from '../../constants/brandSurfaces';

const fontFamily = "'DM Sans', 'Segoe UI', sans-serif";

/** Trade color set as `--trade` on PageWrapper (see ServiceSearchFlow.styled.js). */
const trade = 'var(--trade, #0d4efe)';
const tint = (percent) => `color-mix(in srgb, ${trade} ${percent}%, #ffffff)`;

// ─── PAGE SHELL ─────────────────────────────────────────────────────────────

export const PageWrapper = styled(Box)(() => ({
    minHeight: '100vh',
    background: MINT_MIST,
    fontFamily,
}));

// ─── HEADER ──────────────────────────────────────────────────────────────────

export const HeaderSection = styled(Box)(({ theme }) => ({
    background: tint(8),
    borderBottom: `1px solid ${tint(22)}`,
    padding: '22px 32px 24px',
    [theme.breakpoints.down('sm')]: {
        padding: '18px 16px 20px',
    },
}));

export const HeaderInner = styled(Box)(() => ({
    maxWidth: '1100px',
    margin: '0 auto',
}));

export const HeaderTitle = styled('h1')(() => ({
    margin: 0,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(1.3rem, 3vw, 1.85rem)',
    fontWeight: 700,
    color: '#1a1a2e',
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
}));

export const AnswerChipRow = styled(Box)(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '14px',
}));

/** Editable search answer (trade / task / timing) */
export const AnswerChip = styled(ButtonBase)(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '999px',
    background: '#fff',
    border: `1px solid ${tint(35)}`,
    color: '#1a1a2e',
    fontFamily,
    fontSize: '0.82rem',
    fontWeight: 600,
    transition: 'background 0.15s ease, border-color 0.15s ease',
    '& .answer-chip-kind': {
        fontWeight: 500,
        color: '#5a6478',
    },
    '& svg': {
        fontSize: 14,
        color: '#5a6478',
    },
    '&:hover, &.Mui-focusVisible': {
        borderColor: trade,
    },
}));

// ─── FILTER / SORT BAR ───────────────────────────────────────────────────────

export const FilterBar = styled(Box)(({ theme }) => ({
    background: '#fff',
    borderBottom: '1px solid #E5E7EB',
    padding: '12px 32px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    [theme.breakpoints.down('sm')]: {
        padding: '10px 16px',
    },
}));

export const FilterBarInner = styled(Box)(() => ({
    maxWidth: '1100px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
}));

export const FilterLabel = styled('span')(() => ({
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginRight: '4px',
}));

export const FilterPillButton = styled('button', {
    shouldForwardProp: (prop) => prop !== '$active',
})(({ $active }) => ({
    padding: '6px 14px',
    borderRadius: '20px',
    border: $active ? '1.5px solid #0D4EFE' : '1.5px solid #E5E7EB',
    background: $active ? '#EFF6FF' : '#fff',
    color: $active ? '#0D4EFE' : '#6B7280',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    fontFamily,
    '&:hover': {
        borderColor: '#0D4EFE',
        color: '#0D4EFE',
    },
}));

export const SortGroup = styled(Box)(() => ({
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
}));

export const SortLabel = styled('span')(() => ({
    fontSize: '0.75rem',
    color: '#9CA3AF',
    fontWeight: 600,
}));

export const SortButton = styled('button', {
    shouldForwardProp: (prop) => prop !== '$active',
})(({ $active }) => ({
    padding: '5px 11px',
    borderRadius: '8px',
    border: $active ? '1.5px solid #0D4EFE' : '1.5px solid #E5E7EB',
    background: $active ? '#EFF6FF' : 'transparent',
    color: $active ? '#0D4EFE' : '#6B7280',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily,
    transition: 'all 0.15s',
    '&:hover': {
        borderColor: '#0D4EFE',
        color: '#0D4EFE',
    },
}));

// ─── RESULTS ─────────────────────────────────────────────────────────────────

export const ResultsContainer = styled(Box)(({ theme }) => ({
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '24px 32px 40px',
    [theme.breakpoints.down('sm')]: {
        padding: '16px 16px 32px',
    },
}));

/** Explains a relaxed filter ("No pros list X yet; here are 9 electricians.") */
export const ResultsNotice = styled(Box)(() => ({
    marginBottom: '18px',
    padding: '12px 16px',
    borderRadius: '12px',
    background:
        'linear-gradient(135deg, rgba(1, 235, 178, 0.09) 0%, rgba(13, 78, 254, 0.06) 100%)',
    border: '1px solid rgba(13, 78, 254, 0.14)',
    fontSize: '0.9rem',
    lineHeight: 1.5,
    color: '#374151',
    '& strong': {
        color: '#1a1a2e',
    },
}));

/** Desktop: 3×3 cards; narrower viewports collapse columns */
export const ProvidersGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
    },
}));

export const ResultsPaginationWrap = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    marginTop: '20px',
}));

export const ResultsPaginationSummary = styled('p')(() => ({
    margin: 0,
    fontSize: '0.82rem',
    color: '#6B7280',
    fontWeight: 500,
}));

export const EmptyState = styled(Box)(() => ({
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6B7280',
}));

export const EmptyStateTitle = styled('h2')(() => ({
    margin: '0 0 8px',
    fontSize: '1.15rem',
    color: '#111827',
    fontWeight: 700,
}));

export const EmptyStateText = styled('p')(() => ({
    margin: 0,
    fontSize: '0.9rem',
}));

// ─── PROVIDER CARD ───────────────────────────────────────────────────────────

export const CardWrapper = styled('article')(() => ({
    background: '#fff',
    borderRadius: '16px',
    border: '1px solid #E5E7EB',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
    fontFamily,
}));

export const CardAccentBar = styled(Box, {
    shouldForwardProp: (prop) => prop !== '$start' && prop !== '$end',
})(({ $start, $end }) => ({
    height: '4px',
    background: `linear-gradient(90deg, ${$start}, ${$end})`,
}));

export const CardBody = styled(Box)(() => ({
    padding: '18px 18px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
}));

export const CardTopRow = styled(Box)(() => ({
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start',
}));

export const CardAvatar = styled(Box, {
    shouldForwardProp: (prop) => prop !== '$start' && prop !== '$end',
})(({ $start, $end }) => ({
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    background: `linear-gradient(135deg, ${$start}, ${$end})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
    fontWeight: 700,
    fontSize: '1rem',
    letterSpacing: '0.02em',
    overflow: 'hidden',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
}));

export const CardMeta = styled(Box)(() => ({
    flex: 1,
    minWidth: 0,
}));

/** Full name always visible (wraps; no height clamp) */
export const CardName = styled('h2')(() => ({
    margin: 0,
    fontSize: '1.06rem',
    fontWeight: 700,
    color: '#111827',
    lineHeight: 1.28,
    letterSpacing: '-0.02em',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
}));

export const LocationText = styled('p')(() => ({
    margin: '2px 0 0',
    fontSize: '0.78rem',
    color: '#6B7280',
}));

export const RatingsBlock = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginTop: '6px',
}));

export const NoReviews = styled('span')(() => ({
    fontSize: '0.75rem',
    color: '#9CA3AF',
}));

/** Task match / price panel */
export const CardPricePanel = styled(Box)(() => ({
    borderRadius: '10px',
    padding: '10px 12px',
    background:
        'linear-gradient(135deg, rgba(1, 235, 178, 0.08) 0%, rgba(13, 78, 254, 0.06) 100%)',
    border: '1px solid rgba(13, 78, 254, 0.16)',
}));

export const CardTagline = styled('p')(() => ({
    margin: '0 0 6px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    fontSize: '0.82rem',
    lineHeight: 1.4,
    color: '#374151',
    '& svg': {
        fontSize: 16,
        color: '#16a34a',
        flexShrink: 0,
        marginTop: 1,
    },
}));

export const CardPrice = styled('div')(() => ({
    fontSize: '1.05rem',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: '#0D4EFE',
}));

export const CardPriceCaption = styled('span')(() => ({
    display: 'block',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#6B7280',
    marginTop: '2px',
}));

export const BadgeRow = styled(Box)(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
}));

export const CardBadge = styled('span', {
    shouldForwardProp: (prop) => prop !== '$tone',
})(({ $tone }) => {
    const tones = {
        green: { background: '#D1FAE5', color: '#065F46' },
        blue: { background: '#EFF6FF', color: '#0D4EFE' },
        neutral: { background: '#F3F4F6', color: '#4B5563' },
    };
    const tone = tones[$tone] || tones.neutral;
    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '0.72rem',
        fontWeight: 600,
        padding: '3px 9px',
        borderRadius: '20px',
        whiteSpace: 'nowrap',
        ...tone,
        '& svg': { fontSize: 13 },
    };
});

export const CardCTARow = styled(Box)(() => ({
    padding: '14px 18px 16px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    borderTop: '1px solid #F3F4F6',
    marginTop: '14px',
    '& > :first-of-type': {
        gridColumn: '1 / -1',
    },
}));

const ctaBase = {
    borderRadius: '10px',
    padding: '10px 8px',
    fontSize: '0.82rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontFamily,
    textDecoration: 'none',
    '& svg': { fontSize: 16 },
};

export const PrimaryCtaButton = styled('button')(() => ({
    ...ctaBase,
    background: 'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
    color: '#fff',
    border: 'none',
    boxShadow: '0 4px 14px rgba(13, 78, 254, 0.25)',
    transition: 'opacity 0.15s',
    '&:hover': {
        opacity: 0.92,
    },
}));

export const SecondaryCtaLink = styled('a')(() => ({
    ...ctaBase,
    background: 'transparent',
    color: '#0D4EFE',
    border: '1.5px solid rgba(13, 78, 254, 0.45)',
    transition: 'background 0.15s',
    '&:hover': {
        background: '#EFF6FF',
    },
}));

/** Full-page wait while providers load */
export const ProvidersLoadingState = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 72px)',
    padding: '48px 24px',
    gap: '20px',
    textAlign: 'center',
    background:
        'linear-gradient(180deg, rgba(1, 235, 178, 0.08) 0%, rgba(13, 78, 254, 0.05) 42%, #F9FAFB 100%)',
}));

export const ProvidersLoadingTitle = styled('p')(() => ({
    margin: 0,
    fontFamily,
    fontSize: '1.125rem',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: '#1a1a2e',
}));
